// src/components/product/ProductDetailClient.tsx
"use client";

import ProductJsonLd from "../seo/ProductJsonLd";
import { useEffect, useMemo, useState } from "react";
import Container from "@/components/layout/Container";
import { getProductBySlug } from "@/lib/api/products";
import type { Product } from "@/types/product";
import SmartImg from "@/components/product/SmartImg";
import InquiryModal from "@/components/inquiries/InquiryModal";

export default function ProductDetailClient({
  slug,
  initialProduct = null,
}: {
  slug: string;
  initialProduct?: Product | null;
}) {
  // ✅ Start with the server product if provided
  const [product, setProduct] = useState<Product | null>(initialProduct);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // which image is currently shown as the big “hero”
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    async function run() {
      // ✅ If we already have it (from server), skip the duplicate fetch
      if (initialProduct) {
        setErr(null);
        return;
      }

      try {
        const p = await getProductBySlug(slug);
        if (!alive) return;
        setProduct((p as Product) ?? null);
        setErr(null);
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? "Failed to load product");
        setProduct(null);
      }
    }

    run();
    return () => {
      alive = false;
    };
    // important: depend on slug + whether we got an initial product
  }, [slug, initialProduct]);

  const images = useMemo(() => {
    const list = (product?.images ?? [])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return list;
  }, [product]);

  // when product/images change, pick a default hero once
  useEffect(() => {
    if (!images.length) {
      setSelectedImageId(null);
      return;
    }

    // keep selection if it still exists, otherwise fall back to first
    setSelectedImageId((curr) => {
      if (curr && images.some((x) => x.id === curr)) return curr;
      return images[0].id;
    });
  }, [images]);

  const hero = useMemo(() => {
    if (!images.length) return null;
    if (!selectedImageId) return images[0];
    return images.find((x) => x.id === selectedImageId) ?? images[0];
  }, [images, selectedImageId]);

  if (err) {
    return (
      <Container>
        <div className="py-10">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            {err}
          </div>
        </div>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <div className="py-10">
          <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
            Loading...
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="py-8">
        <ProductJsonLd product={product} />

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Image panel */}
          <div className="rounded-lg border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="aspect-square w-full overflow-hidden rounded-md bg-neutral-100 dark:bg-neutral-800">
              {hero ? (
                <SmartImg
                  src={hero.url}
                  alt={hero.alt ?? product.name}
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
                  No image
                </div>
              )}
            </div>

            {images.length > 1 ? (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.slice(0, 10).map((img) => {
                  const active = hero?.id === img.id;
                  return (
                    <button
                      key={`${img.id}:${img.url}`}
                      type="button"
                      onClick={() => setSelectedImageId(img.id)}
                      className={[
                        "aspect-square overflow-hidden rounded-md bg-neutral-100 dark:bg-neutral-800",
                        "border transition",
                        active
                          ? "border-neutral-900 dark:border-neutral-100"
                          : "border-transparent hover:border-neutral-300 dark:hover:border-neutral-700",
                      ].join(" ")}
                      aria-label={`Show image ${img.id}`}
                    >
                      <SmartImg
                        src={img.url}
                        alt={img.alt ?? product.name}
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* Info panel */}
          <div className="rounded-lg border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              {product.name}
            </h1>

            <div className="mt-3 flex items-center gap-3">
              <div className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                ${product.price.toFixed(2)}
              </div>

              {product.compareAtPrice ? (
                <div className="text-neutral-500 line-through dark:text-neutral-400">
                  ${product.compareAtPrice.toFixed(2)}
                </div>
              ) : null}
            </div>

            <div className="mt-3 text-sm text-neutral-700 dark:text-neutral-300">
              {product.condition ?? "—"}
              {product.brand ? ` • ${product.brand}` : ""}
            </div>

            {product.description ? (
              <p className="mt-5 whitespace-pre-line text-sm text-neutral-700 dark:text-neutral-300">
                {product.description}
              </p>
            ) : null}

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setOpen(true)}
                className="rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
              >
                Inquire
              </button>
            </div>
          </div>
        </div>

        <InquiryModal open={open} onClose={() => setOpen(false)} product={product} />
      </div>
    </Container>
  );
}
